/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SnapDialog } from '@one-snap/sparkles';
import PropTypes from 'prop-types';
import getDefaultCredentials from '../../config/defaultCredentials';
import './CheckoutStyles.css';
import SampleInfoCard from './SampleInfoCard';

function Checkout({
  isModalVisible,
  handleCancel,
  cart,
  salesTax,
  shipping,
  discount,
  env,
  token,
  audience,
  currency
}) {
  if (!env || !token || !cart) return null;

  const navigate = useNavigate();

  const [snapSdkUrl, setSdkUrl] = useState('');
  const [asLowAsPercent, setAsLowAsPercent] = useState('0.00');
  const [isCardVisible, setCardVisible] = useState(false);
  const isScriptLoaded = useRef(false);

  const calculateSubTotal = () =>
    cart.reduce(
      (total, item) =>
        total + item.quantity * parseFloat(item.description.slice(1)),
      0
    );

  const calculateTotal = () => {
    const subTotal = cart.reduce(
      (total, item) =>
        total +
        parseFloat(item.description.replace(/[^0-9.-]+/g, '')) * item.quantity,
      0
    );
    return (subTotal + salesTax + shipping - discount).toFixed(2);
  };

  const proceedToApiPage = (approvedAppId) => {
    const propsToPass = {
      env,
      audience,
      token,
      approvedAppId
    };
    navigate('/stores/sdk/api', { state: propsToPass });
  };

  useEffect(() => {
    const loadSnapSDK = () => {
      const script = document.createElement('script');
      const { sdkUrl } = getDefaultCredentials(env);

      if (sdkUrl) {
        script.src = sdkUrl;
        script.async = true;

        script.onload = () => {
          window.snap.init(token);

          // eslint-disable-next-line no-use-before-define
          calculateAlaPayment();
          try {
            const checkoutButton = document.getElementById(
              'snap-checkout-button'
            );
            if (checkoutButton && !isScriptLoaded.current) {
              // eslint-disable-next-line no-use-before-define
              initTheSdk();
              isScriptLoaded.current = true;
            }
          } catch (error) {
            console.error('There is an error loading the SDK', error);
          }
        };

        document.body.appendChild(script);
      }
      setSdkUrl(sdkUrl);
    };

    const calculateAlaPayment = () => {
      // eslint-disable-next-line no-undef
      snap
        .calculateAlaPayment(token, {
          frequency: 'W',
          items: [
            {
              // eslint-disable-next-line no-use-before-define
              price: Number(calculateTotal()),
              itemId: 'ABC',
              sku: 'D1234'
            }
          ]
        })
        .then((res) => {
          // console.log(res.data.items[0].paymentScheduleAmount);
          setAsLowAsPercent(res.data.items[0].paymentScheduleAmount);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    };

    const initTheSdk = () => {
      const transaction = {
        customerInformation: {
          dobDate: '',
          customerId: '',
          customerIdType: '',
          mobilePhone: '7676767676',
          mobilePhoneCountry: '',
          email: 'test@snapfinance.com',
          firstName: 'test',
          lastName: 'test',
          billingAddress: {
            streetAddress: '15063 E 14th St',
            city: 'San Leandro',
            postalCode: '90201',
            state: 'CA',
            country: 'US',
            unit: '1'
          }
        },
        cartInformation: {
          currencyCode: currency,
          taxAmount: salesTax.toFixed(2),
          shippingAmount: shipping.toFixed(2),
          // eslint-disable-next-line no-use-before-define
          totalAmount: calculateTotal(),
          discountAmount: discount.toFixed(2),
          orderId: 'test001',
          items: [
            {
              // eslint-disable-next-line no-use-before-define
              price: calculateSubTotal().toFixed(2),
              itemId: 'test001',
              description: 'test001',
              sku: 'test001',
              quantity: 1,
              leasable: true
            }
          ],
          shippingAddress: {
            streetAddress: '15063 E 14th St',
            city: 'San Leandro',
            postalCode: '90201',
            state: 'CA',
            country: 'US',
            unit: '1'
          }
        }
      };
      // eslint-disable-next-line no-undef
      window.snap
        .checkoutButton({
          style: {
            color: 'dark',
            shape: 'pill',
            height: 45,
            cursor: 'pointer !important'
          },
          onClick(data, actions) {
            // This action MUST be called to launch the checkout process.
            setCardVisible(false);
            return actions.launchCheckout(transaction);
          },
          // eslint-disable-next-line no-unused-vars
          onApproved(data, actions) {
            setCardVisible(false);
            // console.log('applicationId', data.applicationId);
            proceedToApiPage(data.applicationId);
          },
          // eslint-disable-next-line no-unused-vars
          onError(data, actions) {
            setCardVisible(false);
            console.log(`Error message to 
            merchant site from snap 
            checkout process: ${data}`);
          }
        })
        .render();
      // eslint-disable-next-line no-undef
      window.snap
        .checkoutMark({
          style: {
            color: 'dark',
            height: 45
          }
        })
        .render();
    };

    loadSnapSDK();

    return () => {
      const existingScript = document.querySelector(
        `script[src^="${snapSdkUrl}"]`
      );
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [env, token, isScriptLoaded]);

  const handleAsLowAs = async () => {
    // eslint-disable-next-line no-undef
    const res = await snap.calculateAlaPayment(token, {
      frequency: 'W',
      items: [
        {
          price: Number(calculateTotal()),
          itemId: 'ABC',
          sku: 'D1234'
        }
      ]
    });

    const paymentEstimateData = {
      ...res.data.items[0],
      totalCost: res.data.totalCost,
      costOfLease: res.data.costOfLease
    };

    window.snap.launch({
      token,
      marketing: {
        treatment: 'AS_LOW_AS'
      },
      data: paymentEstimateData
    });
  };

  const handleGetApproved = () => {
    window.snap.launch({
      token,
      marketing: {
        treatment: 'PRE_APPROVAL'
      }
    });
  };

  return (
    <div>
      <SnapDialog
        data-testid="checkout-partner-modal"
        open={isModalVisible}
        onClose={handleCancel}
      >
        <div className="store-modal">
          <div className="store-modal-title">Are these details correct?</div>
          <div className="store-modal-subtitle">
            Review below and go back to make changes if needed.
          </div>
          <div className="shopping-cart-card">
            <div className="store-address-panel">
              <div>
                <b>Shipping Address</b>
              </div>
              <div>John Doe</div>
              <div className="store-add-item">
                123 Main St. San Jose, CA 95128
              </div>
            </div>
            <div className="store-address-panel">
              <div>
                <b>Products</b>
              </div>
              {cart.length === 0 ? (
                <div>No items in the cart.</div>
              ) : (
                cart.map((item, index) => (
                  <div
                    key={item.id}
                    className="store-cart-item"
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <div>
                      {index + 1}. {item.fancyName}
                    </div>
                    <div>
                      {item.quantity} x $
                      {parseFloat(
                        item.description.replace(/[^0-9.-]+/g, '')
                      ).toFixed(2)}
                    </div>
                  </div>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div
                className="store-cart-subtotal"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '10px',
                  fontWeight: 'bold'
                }}
              >
                <span />
                <span>
                  Sub-Total&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;$
                  {cart
                    .reduce(
                      (total, item) =>
                        total +
                        parseFloat(item.description.replace(/[^0-9.-]+/g, '')) *
                          item.quantity,
                      0
                    )
                    .toFixed(2)}
                </span>
              </div>
            )}
          </div>
          <div
            className="sale-numbers"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <span>
              <b>Sales Tax</b>
            </span>
            <span>${salesTax.toFixed(2)}</span>
          </div>
          <div
            className="sale-numbers"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <span>Shipping</span>
            <span>${shipping.toFixed(2)}</span>
          </div>
          <div
            className="sale-numbers"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <span>Discount</span>
            <span>${discount.toFixed(2)}</span>
          </div>
          <div
            className="sale-numbers sale-numbers-total"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold'
            }}
          >
            <span>
              <b>Total Amount</b>
            </span>
            <span>${calculateTotal()}</span>
          </div>
          <div className="lease-to-own-snap">
            <div
              id="snap-checkout-mark"
              style={{ marginBottom: '10px', marginRight: '80px' }}
            />
            <div
              id="snap-checkout-button"
              style={{
                cursor: 'pointer !important'
              }}
            />
          </div>
          <div className="lease-to-own-section">
            <div className="lease-txt">
              Lease for as low as ${asLowAsPercent}/week.{' '}
              <span className="lease-learn-more" onClick={handleAsLowAs}>
                Learn More
              </span>
            </div>
            <div className="lease-txt">
              Snap offers lease-to-own financing.{' '}
              <span className="lease-learn-more" onClick={handleGetApproved}>
                Get Approved
              </span>
            </div>
          </div>
        </div>
      </SnapDialog>
      <SampleInfoCard
        isCardVisible={isCardVisible}
        setCardVisible={setCardVisible}
      />
    </div>
  );
}

Checkout.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      fancyName: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired
    })
  ).isRequired,
  salesTax: PropTypes.number.isRequired,
  shipping: PropTypes.number.isRequired,
  discount: PropTypes.number.isRequired,
  env: PropTypes.string.isRequired,
  audience: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired
};

export default Checkout;
